import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Upload, FileText, Calendar, AlertCircle, CheckCircle, X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const CustomerUploadList = () => {
  const [uploads, setUploads] = useState([]);
  const [file, setFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/customer-uploads/');
      setUploads(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Выберите файл');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/settings/customer-uploads/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Файл загружен и будет обработан');
      closeModal();
      fetchUploads();
    } catch (error) {
      toast.error('Ошибка загрузки файла');
    }
  };

  const handleProcess = async (id) => {
    try {
      await api.post(`/settings/customer-uploads/${id}/process/`);
      toast.success('Файл обработан');
      fetchUploads();
    } catch (error) {
      toast.error('Ошибка обработки файла');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить загрузку?')) {
      try {
        await api.delete(`/settings/customer-uploads/${id}/`);
        toast.success('Загрузка удалена');
        fetchUploads();
      } catch (error) {
        toast.error('Ошибка удаления загрузки');
      }
    }
  };

  const openAddModal = () => {
    setFile(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFile(null);
  };

  const getStatusBadge = (upload) => {
    if (upload.processed) {
      return (
        <span className={`${styles.statusBadge} ${styles.processed}`}>
          <CheckCircle size={12} style={{ marginRight: '0.25rem' }} />
          Обработан
        </span>
      );
    } else if (upload.error_log) {
      return (
        <span className={`${styles.statusBadge} ${styles.error}`}>
          <AlertCircle size={12} style={{ marginRight: '0.25rem' }} />
          Ошибка
        </span>
      );
    } else {
      return (
        <span className={`${styles.statusBadge} ${styles.pending}`}>
          <Upload size={12} style={{ marginRight: '0.25rem' }} />
          Ожидает
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка файлов...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Upload size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Загрузки заказчиков
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить загрузку
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {uploads.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Upload size={48} />
            </div>
            <h3>Нет загрузок</h3>
            <p>Добавьте первый файл для обработки данных заказчиков</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Файл</th>
                <th>Дата загрузки</th>
                <th>Статус</th>
                <th>Дата обработки</th>
                <th>Ошибки</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map(upload => (
                <tr key={upload.id}>
                  <td>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                      color: '#475569',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      width: 'fit-content'
                    }}>
                      #{upload.id}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={16} style={{ color: '#0f766e' }} />
                      <span style={{ fontWeight: '500' }}>
                        {upload.file.split('/').pop()}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Calendar size={14} style={{ color: '#64748b' }} />
                      {new Date(upload.uploaded_at).toLocaleString('ru-RU')}
                    </div>
                  </td>
                  <td>{getStatusBadge(upload)}</td>
                  <td>
                    {upload.processed_at ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} style={{ color: '#64748b' }} />
                        {new Date(upload.processed_at).toLocaleString('ru-RU')}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                    {upload.error_log ? (
                      <div style={{ 
                        color: '#dc2626',
                        background: 'linear-gradient(135deg, #fef2f2, #fecaca)',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem'
                      }}>
                        {upload.error_log}
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    {!upload.processed && (
                      <button
                        className={styles.actionButton}
                        onClick={() => handleProcess(upload.id)}
                        title="Обработать"
                        style={{ marginRight: '0.5rem' }}
                      >
                        <Upload size={16} />
                      </button>
                    )}
                    <button
                      className={styles.actionButton}
                      onClick={() => handleDelete(upload.id)}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Upload size={20} />
                Добавить загрузку
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <FileText size={16} />
                    Файл Excel
                  </label>
                  <div className={styles.fileInputWrapper}>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setFile(e.target.files[0])}
                      id="file-upload"
                    />
                    <button
                      type="button"
                      className={styles.uploadButton}
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      <Upload size={16} />
                      Выбрать файл
                    </button>
                    {file && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        color: '#065f46',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        <FileText size={16} />
                        {file.name}
                      </div>
                    )}
                  </div>
                </div>
                
                <button type="submit" className={styles.submitButton}>
                  <Upload size={16} />
                  Загрузить файл
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerUploadList;